import type {
  ActiveEntityFocus,
  ChatSubjectFocusEntry,
  EntityType,
  InventoryEntityType,
  PortalView,
  SessionCreationModule,
  SessionCreationScope,
  SessionFocusRuntimeEntry,
  SessionFocusRuntimeMap,
  SessionTableFocusView,
} from '#types/types';
import {
  ENTITY_TYPES,
  INVENTORY_ENTITY_TYPES,
  SESSION_TABLE_FOCUS_VIEWS,
} from '#types/types';

const ENTITY_TYPE_SET = new Set<string>(ENTITY_TYPES as readonly string[]);
const INVENTORY_ENTITY_TYPE_SET = new Set<string>(INVENTORY_ENTITY_TYPES as readonly string[]);
const SESSION_TABLE_FOCUS_VIEW_SET = new Set<string>(SESSION_TABLE_FOCUS_VIEWS as readonly string[]);

export const TABLE_FOCUS_ENTITY_ID_PREFIX = '__table__:';
const TABLE_FOCUS_SCOPE = 'table' as const;

// "Agents live in projects": a conversation anchored to a concrete entity in one
// of the collaborative workspace modules is a SHARED, org-level artifact: every
// collaborator resolves to the SAME canonical home conversation and may read and
// contribute to it. The orchestrator ('master') and 'global' chats stay private
// to their owner. This predicate is the single source of truth for that rule, so
// the session-list resume (read) and the TimelineEvent write path agree exactly.
// Source is NOT shared (owner, 2026-09-27): a campaign belongs to its author and its conversation
// to whoever opens it. Shared, the home resolution landed people in a colleague's, and once a
// Fater account in a client's, thread, and wrote into it.
const SHARED_ENTITY_CONVERSATION_MODULES = new Set<string>(['studio', 'analyst', 'slides']);

export const isSharedEntityConversation = (scope: {
  creationModule?: string | null;
  creationEntityType?: string | null;
  creationEntityId?: string | null;
}): boolean =>
  typeof scope.creationEntityType === 'string' &&
  scope.creationEntityType.length > 0 &&
  typeof scope.creationEntityId === 'string' &&
  scope.creationEntityId.length > 0 &&
  SHARED_ENTITY_CONVERSATION_MODULES.has(scope.creationModule ?? '');

// WHICH TOOL A CONVERSATION IS AT HOME IN.
//
// A session is stamped with the module that created it and stays there for life.
// Navigating to a tool must therefore LEAVE a session belonging to a different
// module, or the previous tool's conversation renders beside the new tool's
// workspace. That is what put a Studio project's name in the header and its
// generation prompts in the left panel while the user was looking at the Slides
// deck browser: the Slides surface asks for no chat until a deck is open, but
// showEditorChatPanel opens on an active Project subject as an OR alternative,
// which outranks that gate. Portal only guarded 'master' here, so Master was the
// single pair that could not leak and every other pair could.
//
// Views absent from this map are the leasing Portal's own (properties, deals,
// dashboard...), where 'source' and 'global' conversations live.
const VIEW_SESSION_MODULES: Partial<Record<PortalView, readonly SessionCreationModule[]>> = {
  footfall: ['pulse'],
  slides: ['slides'],
  tables: ['analyst'],
  doctrine: ['doctrine'],
  projects: ['studio'],
};

const PORTAL_OWN_MODULES: readonly SessionCreationModule[] = ['source', 'global'];

/**
 * May a session created by `module` stay active while `view` is shown?
 *
 * 'master' belongs to no view on purpose: the Master console renders over any
 * tool it is carried into, which is the behaviour the original guard existed to
 * stop. It is preserved here by being absent from every list.
 */
export const sessionModuleBelongsToView = (
  module: SessionCreationModule,
  view: PortalView,
): boolean => (VIEW_SESSION_MODULES[view] ?? PORTAL_OWN_MODULES).includes(module);

export const SESSION_TABLE_FOCUS_VIEW_TO_ENTITY_TYPE: Record<SessionTableFocusView, EntityType> = {
  properties: 'Property',
  units: 'Unit',
  marketSignals: 'MarketSignal',
  brands: 'Brand',
  contacts: 'Contact',
  portfolios: 'Portfolio',
  portfolioFits: 'PortfolioFit',
  deals: 'Deal',
  audits: 'Audit',
  legalEntities: 'LegalEntity',
  projects: 'Project',
  organisations: 'Organisation',
};

const SESSION_TABLE_FOCUS_VIEW_LABELS: Record<SessionTableFocusView, { en: string; fr: string }> = {
  properties: { en: 'Assets Table', fr: 'Table des actifs' },
  units: { en: 'Units Table', fr: 'Table des unites' },
  marketSignals: { en: 'Signals Table', fr: 'Table des signaux' },
  brands: { en: 'Brands Table', fr: 'Table des marques' },
  contacts: { en: 'Contacts Table', fr: 'Table des contacts' },
  portfolios: { en: 'Portfolios Table', fr: 'Table des portefeuilles' },
  portfolioFits: { en: 'Portfolio Fits Table', fr: 'Table des portfolio fits' },
  deals: { en: 'Deals Table', fr: 'Table des deals' },
  audits: { en: 'Audits Table', fr: 'Table des audits' },
  legalEntities: { en: 'Legal Entities Table', fr: 'Table des entites legales' },
  projects: { en: 'Projects Grid', fr: 'Grille des projets' },
  organisations: { en: 'Organisations Table', fr: 'Table des organisations' },
};

/**
 * Exhaustive support matrix.
 * Any new InventoryEntityType must be explicitly marked as mapped to a table view, or null.
 * This is intentionally strict to make lint/typecheck fail-fast on coverage gaps.
 */
const INVENTORY_ENTITY_TO_SESSION_TABLE_FOCUS_VIEW: Record<InventoryEntityType, SessionTableFocusView | null> = {
  Property: 'properties',
  Floor: null,
  Unit: 'units',
  Brand: 'brands',
  Portfolio: 'portfolios',
  LegalEntity: 'legalEntities',
  Deal: 'deals',
  ChatSession: null,
  TimelineEvent: null,
  GeneratedDocument: null,
  BrandLocation: null,
  LegalEntityFinancialRecord: null,
  MarketSignal: 'marketSignals',
  Campaign: null,
  Audit: 'audits',
  SlideCorrection: null,
  SourcingCampaign: null,
  PortfolioFitCampaign: null,
  PortfolioFit: 'portfolioFits',
  Lead: null,
  Project: 'projects',
  Organisation: 'organisations',
  Team: null,
  User: null,
  Notification: null,
  Contact: 'contacts',
  UniversalFact: null,
  StudioWorkspace: null,
  StudioLayer: null,
  StudioWorkspaceVersion: null,
  CreditGrant: null,
};

const normalizeNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeEntityType = (value: unknown): EntityType | null => {
  const normalized = normalizeNonEmptyString(value);
  if (!normalized || !ENTITY_TYPE_SET.has(normalized)) return null;
  return normalized as EntityType;
};

const isInventoryEntityType = (value: unknown): value is InventoryEntityType => {
  const normalized = normalizeNonEmptyString(value);
  return normalized !== null && INVENTORY_ENTITY_TYPE_SET.has(normalized);
};

export const resolveSessionTableFocusView = (
  value: unknown
): SessionTableFocusView | null => {
  const normalized = normalizeNonEmptyString(value);
  if (!normalized || !SESSION_TABLE_FOCUS_VIEW_SET.has(normalized)) return null;
  return normalized as SessionTableFocusView;
};

export const buildSessionTableFocusEntityId = (view: SessionTableFocusView): string =>
  `${TABLE_FOCUS_ENTITY_ID_PREFIX}${view}`;

export const parseSessionTableFocusViewFromEntityId = (
  entityId: unknown
): SessionTableFocusView | null => {
  const normalized = normalizeNonEmptyString(entityId);
  if (!normalized) return null;
  if (!normalized.startsWith(TABLE_FOCUS_ENTITY_ID_PREFIX)) return null;
  const candidate = normalized.slice(TABLE_FOCUS_ENTITY_ID_PREFIX.length).trim();
  return resolveSessionTableFocusView(candidate);
};

export const getSessionTableFocusViewForEntityType = (
  entityType: EntityType | null | undefined
): SessionTableFocusView | null => {
  if (!entityType) return null;
  if (!isInventoryEntityType(entityType)) return null;
  return INVENTORY_ENTITY_TO_SESSION_TABLE_FOCUS_VIEW[entityType];
};

/**
 * The interface entity type of an anchor or a focused subject, translated to the PRISMA MODEL
 * MPRACE resolves against. The two are not the same vocabulary, which is why this table exists at
 * all, and a type that reaches MPRACE without a model behind it is dropped in SILENCE: `addSeed`
 * returns early on `isEntityTypeAllowed`, and the resolution bails on `if (!modelDmmf)`. Anchors
 * coerced anywhere in the pipeline become MPRACE seeds, so an untranslated one is lost without a
 * word.
 *
 * The two campaign granularities are one table, `Campaign`, and only that name has ever been a
 * Prisma model on this side. The interface keeps emitting `SourcingCampaign` and
 * `PortfolioFitCampaign` (SourcingCampaignRenderer.tsx and PortfolioFitCampaignRenderer.tsx focus
 * on exactly those strings), so the translation happens here rather than at every call site.
 *
 * This is the ONE implementation: it used to be copied at every consumer, and the copies diverged
 * into three different tables.
 */
export const normalizeAnchorEntityType = (entityType: string): string => {
  const normalized = entityType.replace(/[_\s-]/g, '').toLowerCase();
  if (normalized === 'documenttemplate') return 'DocumentTemplate';
  if (normalized === 'audit') return 'DataAudit';
  if (normalized === 'sourcingcampaign' || normalized === 'portfoliofitcampaign') return 'Campaign';
  if (normalized === 'studioworkspace') return 'Workspace';
  if (normalized === 'studiolayer') return 'Layer';
  if (normalized === 'studioworkspaceversion') return 'WorkspaceVersion';
  return entityType;
};

export const resolveTableFocusFromPortalView = (
  view: SessionTableFocusView | PortalView | null | undefined
): ActiveEntityFocus | null => {
  if (!view) return null;
  if (!SESSION_TABLE_FOCUS_VIEW_SET.has(view)) return null;
  const tableView = view as SessionTableFocusView;
  return {
    entityType: SESSION_TABLE_FOCUS_VIEW_TO_ENTITY_TYPE[tableView],
    entityId: buildSessionTableFocusEntityId(tableView),
    focusScope: TABLE_FOCUS_SCOPE,
    tableView,
  };
};

export const resolvePortalViewFromTableFocus = (
  subject: ActiveEntityFocus | ChatSubjectFocusEntry | null | undefined
): SessionTableFocusView | null => {
  if (!subject) return null;
  if (subject.focusScope === TABLE_FOCUS_SCOPE) {
    const directView = resolveSessionTableFocusView(subject.tableView);
    if (directView) return directView;
    const fromEntity = parseSessionTableFocusViewFromEntityId(subject.entityId);
    if (fromEntity) return fromEntity;
    return getSessionTableFocusViewForEntityType(subject.entityType);
  }
  const fromExplicit = resolveSessionTableFocusView(subject.tableView);
  if (fromExplicit) return fromExplicit;
  return parseSessionTableFocusViewFromEntityId(subject.entityId);
};

export const isTableFocusSubject = (
  subject: ActiveEntityFocus | ChatSubjectFocusEntry | null | undefined
): boolean => Boolean(resolvePortalViewFromTableFocus(subject));

// The active session's focused subject, read straight off kernel store state. Use this
// as a useKernelStore selector instead of re-deriving the registry lookup inline: it was
// hand-written thirteen times across the Portal, and every copy had to be kept in step
// with the ?? null / undefined convention its callers expected.
export const selectActiveSubjectFromInventoryState = (
  state: {
    activeChatSessionId?: string | null;
    inventory: { ChatSession: { registry: Record<string, { activeSubject?: ActiveEntityFocus | null } | undefined> } };
  }
): ActiveEntityFocus | null => {
  const activeId = state.activeChatSessionId;
  if (!activeId) return null;
  return state.inventory.ChatSession.registry[activeId]?.activeSubject ?? null;
};

export const isEntityFocusSubject = (
  subject: ActiveEntityFocus | null | undefined
): subject is ActiveEntityFocus => {
  if (!subject) return false;
  if (isTableFocusSubject(subject)) return false;
  const entityType = normalizeEntityType(subject.entityType);
  const entityId = normalizeNonEmptyString(subject.entityId);
  return Boolean(entityType && entityId);
};

export const normalizeSubjectFocus = (
  value: unknown
): ActiveEntityFocus | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;

  const focusActionType = normalizeNonEmptyString(record.focusActionType) ?? undefined;
  const rawFocusScope = normalizeNonEmptyString(record.focusScope);
  const tableViewFromField = resolveSessionTableFocusView(record.tableView);
  const tableViewFromEntityId = parseSessionTableFocusViewFromEntityId(record.entityId);

  const entityType =
    normalizeEntityType(record.entityType) ??
    (tableViewFromField ? SESSION_TABLE_FOCUS_VIEW_TO_ENTITY_TYPE[tableViewFromField] : null) ??
    (tableViewFromEntityId ? SESSION_TABLE_FOCUS_VIEW_TO_ENTITY_TYPE[tableViewFromEntityId] : null);

  const shouldResolveAsTableFocus =
    rawFocusScope === TABLE_FOCUS_SCOPE || Boolean(tableViewFromField) || Boolean(tableViewFromEntityId);

  if (shouldResolveAsTableFocus) {
    const tableView =
      tableViewFromField ??
      tableViewFromEntityId ??
      getSessionTableFocusViewForEntityType(entityType);
    if (!tableView) return null;
    return {
      entityType: entityType ?? SESSION_TABLE_FOCUS_VIEW_TO_ENTITY_TYPE[tableView],
      entityId: buildSessionTableFocusEntityId(tableView),
      focusScope: TABLE_FOCUS_SCOPE,
      tableView,
      ...(focusActionType ? { focusActionType: focusActionType as ActiveEntityFocus['focusActionType'] } : {}),
    };
  }

  const entityId = normalizeNonEmptyString(record.entityId);
  if (!entityType || !entityId) return null;

  // The campaign binding must survive normalization: this function sits on every path a subject
  // takes between the DB, the DTO and the store, so dropping the field here would silently unbind
  // every campaign conversation on its next round-trip.
  const campaignId = normalizeNonEmptyString(record.campaignId) ?? undefined;

  return {
    entityType,
    entityId,
    ...(rawFocusScope === 'entity' ? { focusScope: 'entity' as const } : {}),
    ...(focusActionType ? { focusActionType: focusActionType as ActiveEntityFocus['focusActionType'] } : {}),
    ...(campaignId ? { campaignId } : {}),
  };
};

// Creation scope: WHERE a session is born ("agents live in projects").
// Derived ONCE at creation from the subject of the surface the user is
// standing in; never re-derived later (scope is the session's immutable home,
// the subject may wander afterwards). Project subjects home the session in
// Studio; any other entity/table subject homes it in Source; no subject means
// a global conversation. Surfaces without an entity subject that are NOT
// global (e.g. Analyst) pass an explicit scope instead of this derivation.
export const deriveCreationScopeFromSubject = (
  subject: ActiveEntityFocus | null | undefined
): SessionCreationScope => {
  const normalized = normalizeSubjectFocus(subject);
  if (!normalized) {
    return { module: 'global', entityType: null, entityId: null };
  }
  if (isTableFocusSubject(normalized)) {
    // Table focus entityIds are synthetic ("__table__:…"), keep the type only.
    // The projects table is a Studio surface (module-level, no single project);
    // every other table is Source/leasing browsing.
    return normalized.entityType === 'Project'
      ? { module: 'studio', entityType: 'Project', entityId: null }
      : { module: 'source', entityType: normalized.entityType, entityId: null };
  }
  return normalized.entityType === 'Project'
    ? { module: 'studio', entityType: 'Project', entityId: normalized.entityId }
    : { module: 'source', entityType: normalized.entityType, entityId: normalized.entityId };
};

export const toSubjectFocusHistoryEntry = (
  subject: ActiveEntityFocus,
  focusedAt: string
): ChatSubjectFocusEntry => {
  if (isTableFocusSubject(subject)) {
    const tableView = resolvePortalViewFromTableFocus(subject);
    return {
      entityType: subject.entityType,
      entityId: subject.entityId,
      focusScope: TABLE_FOCUS_SCOPE,
      ...(tableView ? { tableView } : {}),
      ...(subject.focusActionType ? { focusActionType: subject.focusActionType } : {}),
      focusedAt,
    };
  }
  return {
    entityType: subject.entityType,
    entityId: subject.entityId,
    ...(subject.focusActionType ? { focusActionType: subject.focusActionType } : {}),
    // Replaying an entry through setActiveSubject must not strip a campaign binding.
    ...(subject.campaignId ? { campaignId: subject.campaignId } : {}),
    focusedAt,
  };
};

export const buildSubjectFocusHistoryKey = (
  subject: ActiveEntityFocus | ChatSubjectFocusEntry | null | undefined
): string | null => {
  if (!subject) return null;
  const tableView = resolvePortalViewFromTableFocus(subject);
  if (tableView) return `table:${tableView}`;

  const entityType = normalizeEntityType(subject.entityType);
  const entityId = normalizeNonEmptyString(subject.entityId);
  if (!entityType || !entityId) return null;
  return `${entityType}:${entityId}`;
};

export const areSubjectFocusesEquivalent = (
  left: ActiveEntityFocus | null | undefined,
  right: ActiveEntityFocus | null | undefined
): boolean => {
  const leftKey = buildSubjectFocusHistoryKey(left);
  const rightKey = buildSubjectFocusHistoryKey(right);
  if (!leftKey || !rightKey) return false;
  if (leftKey !== rightKey) return false;

  const leftAction = normalizeNonEmptyString(left?.focusActionType);
  const rightAction = normalizeNonEmptyString(right?.focusActionType);
  if (leftAction !== rightAction) return false;

  // A campaign binding IS a subject change even when the entity is the same: launching a campaign
  // re-asserts the very Property subject the draft already had, plus the campaignId. Judged
  // equivalent without this line, setActiveSubject skips the write and the conversation never
  // gets bound to its campaign.
  return normalizeNonEmptyString(left?.campaignId) === normalizeNonEmptyString(right?.campaignId);
};

// TABLE FOCUS RUNTIME: what the "table the user is looking at" looks like on the wire.
//
// The entry published under focusRuntimeByKey['table:<view>'] is read by useAgents and
// normalized into the agent prompt, so its shape is context sent to the LLM. Table.tsx and
// Grid.tsx both publish it; these helpers are the single implementation both use, because the
// two hand-written copies had already drifted (a Date in the filters survived one and was
// flattened to {} by the other).

const toRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

/**
 * Drops `undefined` entries recursively, leaving non-plain objects (Date, Map, class
 * instances) untouched: rebuilding those from their own enumerable keys turns them into `{}`.
 */
export const stripUndefinedFields = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map((entry) => stripUndefinedFields(entry));
  if (!value || typeof value !== 'object') return value;
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value;
  return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, entry]) => {
    if (entry === undefined) return acc;
    acc[key] = stripUndefinedFields(entry);
    return acc;
  }, {});
};

/** The already-published entry, normalized exactly like a fresh payload so the two are comparable. */
export const stripFocusRuntimeUpdatedAt = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (stripUndefinedFields(
        Object.fromEntries(
          Object.entries(value as Record<string, unknown>).filter(([key]) => key !== 'updatedAt')
        )
      ) as Record<string, unknown>)
    : {};

export const toFocusRuntimeMap = (value: unknown): SessionFocusRuntimeMap =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as SessionFocusRuntimeMap)
    : {};

export const buildTableFocusRuntimePayload = (input: {
  entityType: EntityType;
  tableView: SessionTableFocusView;
  presentationMode: 'table' | 'grid';
  viewFilters: unknown;
  pageSize: number;
  visibleIds: readonly string[];
}): Omit<SessionFocusRuntimeEntry, 'updatedAt'> => {
  const filters = stripUndefinedFields(toRecord(input.viewFilters)) as Record<string, unknown>;
  const sortBy = typeof filters.sortBy === 'string' ? filters.sortBy : undefined;
  const sortDir: 'asc' | 'desc' | undefined =
    filters.sortDir === 'asc' || filters.sortDir === 'desc' ? filters.sortDir : undefined;
  return {
    focusScope: TABLE_FOCUS_SCOPE,
    entityType: input.entityType,
    tableView: input.tableView,
    presentationMode: input.presentationMode,
    filters,
    ...(sortBy ? { sortBy } : {}),
    ...(sortDir ? { sortDir } : {}),
    pageSize: input.pageSize,
    visibleIds: input.visibleIds.slice(0, 80),
    totalVisible: input.visibleIds.length,
  };
};

const SESSION_TABLE_FOCUS_PAGE_TITLES: Record<SessionTableFocusView, { en: string; fr: string }> = {
  properties: { en: 'Assets', fr: 'Actifs' },
  units: { en: 'Units', fr: 'Unites' },
  marketSignals: { en: 'Signals', fr: 'Signaux' },
  brands: { en: 'Brands', fr: 'Marques' },
  contacts: { en: 'Contacts', fr: 'Contacts' },
  portfolios: { en: 'Portfolios', fr: 'Portefeuilles' },
  portfolioFits: { en: 'Portfolio fits', fr: 'Portfolio fits' },
  deals: { en: 'Deals', fr: 'Deals' },
  audits: { en: 'Audits', fr: 'Audits' },
  legalEntities: { en: 'Legal entities', fr: 'Entites legales' },
  projects: { en: 'Projects', fr: 'Projets' },
  organisations: { en: 'Organisations', fr: 'Organisations' },
};

/** The heading a table view's page shows itself (the top bar no longer carries it). */
export const getSessionTableFocusPageTitle = (
  view: SessionTableFocusView,
  lang: 'en' | 'fr' = 'en'
): string => SESSION_TABLE_FOCUS_PAGE_TITLES[view][lang];

export const getSessionTableFocusLabel = (
  view: SessionTableFocusView,
  lang: 'en' | 'fr' = 'en'
): string => SESSION_TABLE_FOCUS_VIEW_LABELS[view][lang];
